import type { LatLng } from '../@types';
import type { KioskRegistration } from '../services/types/kioskRegistration';

export interface Theme {
  id: string;
  primary: string;
  secondary: string;
}

export interface Branding {
  site_id: number;
  kiosk_id: string;
  direction: string;
  install_app_cta_image: string;
  install_app_cta_url: string;
  install_app_screen_image: string;
  landing_get_mobile_app_text: string;
  landing_footer_logo_url: string;
  welcome_logo_bgcolor: string;
  landing_search_text: string;
  welcome_logo_url: string;
  welcome_tap_screen_text: string;
}

export interface Landmark {
  id: number;
  name: string;
  short_name: string;
  description: string;
  location: {
    site: number;
    building: number;
    floor: number;
    id: string;
  };
  image_url: string;
  hours: {
    type: string;
  };
}

export interface PlaceGroup {
  site_id: number;
  kiosk_id: string;
  name: string;
  image_url: string;
  bg_color: string;
  type: string;
  places?: number[];
  information_body?: string;
  information_title?: string;
}

export interface KioskPlaceGroup {
  kiosk_id: string;
  directory_tiles: PlaceGroup[];
}

export interface PlaceLocation {
  site: number;
  building: number;
  floor: number;
  id: string;
}

export interface NavInstruction {
  accept_only: boolean;
  image_url?: string;
  message?: string;
  redirect_place_id?: number;
}

export interface Place {
  id: number;
  name: string;
  short_name?: string;
  description: string;
  priority: number;
  tags: string[];
  location: PlaceLocation;
  location_type: string;
  media: {
    image_url: string;
  }[];
  kiosk_image_url: string;
  searchable: string;
  visible: string;
  x?: number;
  y?: number;
  nav_redirect_place_id?: number;
  message?: string;
  nav_instruction?: NavInstruction;
  disclaimer_text?: string;
}

export interface SatellitePlace {
  id: number;
  name: string;
  destination_geo: {
    lat: number;
    lon: number;
  };
  display_geo: {
    lat: number;
    lon: number;
  };
  formatted_display_address: string;
  nav_redirect_place_id?: number;
  kiosk_image_url: string;
  message?: string;
  nav_instruction?: NavInstruction;
  disclaimer_text?: string;
}

export interface NavigationPlaces {
  navDestinationPlace?: Place | SatellitePlace | undefined;
  navStartingPlace?:
    | Place
    | SatellitePlace
    | KioskRegistration
    | LatLng
    | undefined;
}
export interface Floor {
  id: number;
  name: string;
  secondary_name: string;
  map_url: string;
  rotation_angle: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  restrooms: any[];
}

export interface Building {
  id: number;
  name: string;
  short_name: string;
  description: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipcode: string;
    latitude: number;
    longitude: number;
  };
  floors: Floor[];
}

export interface Site {
  id: number;
  name: string;
  short_name: string;
  description: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipcode: string;
    latitude: number;
    longitude: number;
  };
  campus_map: {
    id: number;
    name: string;
    site: number;
    map_url: string;
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  buildings: Building[];
}

export interface Network {
  mobile_enabled: boolean;
  name: string;
  display_name: string;
  sites: Site[];
}

export interface PathLocation {
  i: number;
  f: number;
  k: string;
  x: number;
  y: number;
  lat?: number;
  lng?: number;
}

export interface Banner {
  site_id: number;
  kiosk_id: string;
  duration: number;
  banners: {
    image_url: string;
  }[];
}

export interface PlacePosition extends Place {
  x: number;
  y: number;
}

export enum KioskType {
  ALL_MIGHTY = 'AllMighty',
  BA_TABLET = 'BATablet',
  CTS_DIGITAL_SIGNAGE = 'CTSDigitalSignage',
  ELO_TOUCH = 'EloTouch',
}

export interface Kiosk {
  id: string;
  model: (typeof KioskType)[keyof typeof KioskType]; // Dynamically infer the values from KioskType
}

export interface GoogleMapsStartLocation {
  currentLocation?: {
    lat: number;
    lng: number;
  };
}

export enum ThemeType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum PackageTypes {
  Branding = 'branding',
  Banners = 'banners',
  Landmarks = 'landmarks',
  Network = 'network',
  PlaceGroups = 'placegroups',
  Places = 'places',
  Manifest = 'manifest',
}

// based on src > themes > variables.scss - breakpoints
export enum ScreenSizes {
  SMALL = 640,
  LARGE = 1024,
}

export interface XYtoPOIPayload {
  network: string;
  site: number;
  srcBuilding: number;
  srcFloor: number;
  srcX?: number;
  srcY?: number;
  dstBuilding?: number;
  dstFloor?: number;
  dstKey?: string;
  fileUrl: string;
  navInstructionsFileUrl: string;
  navInstructionsLanguage: string;
}

export interface Ribbon {
  site: number;
  building: number;
  floor: number;
  coordinates: number[];
}

export interface NavStep {
  distance: number;
  type: string;
  instruction: string;
  iconIndex: number;
  polyLine: {
    i: number;
    s: number;
    b: number;
    f: number;
    k: string;
    x: number;
    y: number;
  }[];
}
export interface LegTwoRoute {
  ribbons: Ribbon[];
  navSteps: NavStep[];
}

export interface XYtoPOIPayloadResponse {
  requestId: string;
  success: boolean;
  payload: LegTwoRoute;
}

export interface NearestGeoNodePayload {
  network: string;
  site: number;
  building: number;
  floor: number;
  dstKey: string;
  fileUrl: string;
}

export interface GeoInfo {
  site: number;
  building: number;
  floor: number;
  key: string;
  x: number;
  y: number;
  lat: number;
  lon: number;
}
export interface srcXYPayload {
  site: number;
  building: number;
  floor: number;
  key: string;
  x: number;
  y: number;
}

export interface NearestGeoNodeResponse {
  requestId: string;
  success: boolean;
  payload: {
    geoInfo: GeoInfo;
  };
}

export interface EmailContent {
  html: string;
  text: string;
}

export interface EmailAttachment {
  fileName: string;
  file: string;
}

export interface ShareToEmailPayload {
  recipients: string[];
  destinationName: string;
}
